<template>
  <div class="remote-launch container" :class="this.$route.query.darkMode ? 'dark' : ''">
    <ContentBlock
    :darkMode="this.$route.query.darkMode">
      <template #instructions>
        <h3 class="f3">{{ $t('Remote Launch') }}</h3>
        <p class="f5">{{ $t('First, select the title you want to launch remotely, or select Launcher if you want to bring players back into the launcher. Because you might not have installed the same titles on all your stations, you will see which stations are available for launching the title you selected. Then, pick the stations you need.') }}</p>
      </template>
      <div class="selections">
        <ContentSelection
          :darkMode="this.$route.query.darkMode"
          :content="content"
          :contentSelection="contentSelection"
          :launchChoice="launchChoice"
          @update:contentSelection="onContentSelectionUpdate"
          @update:launchChoice="onLaunchChoiceUpdate"
        />
        <StationSelection
          :bookings="bookings"
          :experiences="experiences"
          :stations="stations"
          :darkMode="this.$route.query.darkMode"
          :stationsIdsWithOngoingSessions="stationsIdsWithOngoingSessions"
          :stationsWithTimerDisabled="stationsWithTimerDisabled"
          :stationsIdsWithContentInstalled="stationsIdsWithContentInstalled"
          :stationIdSelection="stationIdSelection"
          :contentSelection="contentSelection"
          @update:stationIdSelection="onStationIdSelectionUpdate"
        />
      </div>
    </ContentBlock>
          <BottomBar :actionText="$t('Launch')" :valid="valid"  :backRouterLink="{ name: 'overview' }" :darkMode="this.$route.query.darkMode" @save="launch" >
      <div class="bottombarinfo">
        <div class="info-block">
          <div class="label f8">{{ $t('Content') }}</div>
          <div class="value f3">{{ contentTitle }}</div>
        </div>
        <div class="info-block">
          <div class="label f8">{{ $tc('Number of Stations', stationIdSelection.length) }}</div>
          <div class="value f3">{{ stationIdSelection.length + ' ' + $tc('Station | Stations', stationIdSelection.length) }}</div>
        </div>
      </div>
    </BottomBar>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentSelection from './ContentSelection';
import StationSelection from './StationSelection';
import BottomBar from '@/core/shared/components/BottomBar';
import ContentBlock from '@/core/shared/components/ContentBlock';
import moment from 'moment-timezone';

@Component({
  components: {
    ContentSelection,
    StationSelection,
    BottomBar,
    ContentBlock,
  },
  props: {
    locationId: {
      type: String,
      required: true,
    },
    darkMode: {
      type: String,
      required: false,
    },
  },
  watch: {
    currentTime: {
      handler () {
        this.updateStationIdSelectionBasedOnValidity();
      },
      immediate: true,
    },
  },
})
export default class RemoteLaunch extends Vue {
  launchChoice = 'content';
  contentSelection = null;
  stationIdSelection = [];
  stationsIdsWithContentInstalled = null;

  get content () {
    return this.$store.getters['entities/Content/all']().sort((a, b) => {
      if (a.title < b.title) return -1;
      if (b.title < a.title) return 1;
      return 0;
    });
  }

  get contentTitle () {
    if (this.launchChoice === 'launcher') return this.$t('Launcher');
    if (this.contentSelection) return this.contentSelection.title;
    return '-';
  }

  get currentTime () {
    return this.$store.getters.currentTime;
  }

  get bookings () {
    const stationsInAlready = [];
    return this.$store.getters.locationBookingsOnDate({ locationId: this.locationId, date: this.currentTime })
      .reduce((bookingsAlreadyIn, booking) => {
        const foundBooking = booking.bookingStationTimes.find(bookingStationTime => {
          const valid = bookingStationTime._playing || this.isBookingStationTimeEndTimeLessThanFiveMinutesAgoAndIsLatest(bookingStationTime);

          // This is needed because sometimes you'll have a playing booking that is playing in the same 5 minute time window as the previous booking ending
          // We don't want to show both the old ended booking in the 5 minute grace period AND the new playing booking so I need to backtrack through the
          // bookings we already found to show and then see if there's any duplicated stations, then I prioritize the playing station. PHEW.
          if (valid) {
            const station = bookingStationTime.station.id;
            if (stationsInAlready.includes(station)) {
              if (bookingStationTime._playing) {
                const bookingWithStationAlreadyInIndex = bookingsAlreadyIn.findIndex(
                  bookingFind => bookingFind.bookingStationTimes.find(
                    bookingStationTimeFind => bookingStationTimeFind.station.id === station,
                  ),
                );
                bookingsAlreadyIn.splice(bookingWithStationAlreadyInIndex, 1);
              } else {
                return false;
              };
            } else {
              stationsInAlready.push(station);
            }
          }

          return valid;
        });

        if (foundBooking) {
          return [...bookingsAlreadyIn, booking];
        } else {
          return bookingsAlreadyIn;
        }
      }, []);
  }

  isBookingStationTimeEndTimeLessThanFiveMinutesAgoAndIsLatest (bookingStationTime) {
    if (bookingStationTime.endTime) {
      const diff = moment(this.currentTime).diff(moment(bookingStationTime.endTime), 'minutes');
      return diff <= 5 && diff >= 0;
    }
  }

  get experiences () {
    return this.$store.getters.locationExperiences(this.locationId);
  }

  get stationsIdsWithOngoingSessions () {
    return this.bookings.reduce((stations, booking) => {
      return [
        ...stations,
        ...booking.bookingStationTimes
          .map(bookingStationTime => bookingStationTime.station.id),
      ];
    }, []);
  }

  get stations () {
    return this.$store.getters.locationStations(this.locationId);
  }

  get stationsWithTimerDisabled () {
    return this.stations.filter(station => !station.timerEnabled);
  }

  get valid () {
    if (this.launchChoice === 'content' && !this.contentSelection) return false;
    if (this.stationIdSelection.length < 1) return false;
    return true;
  }

  async launch () {
    const stations = this.stationIdSelection.map(station => ({ id: station }));
    try {
      if (this.launchChoice === 'content') {
        this.$store.dispatch('remoteLaunchContent', {
          stations,
          content: { id: this.contentSelection.id },
        });
      } else {
        this.$store.dispatch('remoteLaunchLauncher', stations);
      }

      this.$store.commit('setFlash', {
        type: 'success',
        display: 'flash',
        message: this.$tc('Sending remote launch command to station... | Sending remote launch command to stations...', stations.length),
      });

      this.$router.push({ name: 'overview' });
    } catch (e) { }
  }

  onContentSelectionUpdate (content) {
    this.contentSelection = content;
    this.getContentDetails();
    this.updateStationIdSelectionBasedOnValidity();
  }

  onLaunchChoiceUpdate (launchChoice) {
    this.launchChoice = launchChoice;
    this.getContentDetails();
    this.updateStationIdSelectionBasedOnValidity();
  }

  onStationIdSelectionUpdate (stationIdSelection) {
    this.stationIdSelection = stationIdSelection;
  }

  async getContentDetails () {
    if (this.contentSelection && this.launchChoice === 'content') {
      const contentInStore = this.$store.getters['entities/Content'](this.contentSelection.id);
      const contentWithStationGames = (contentInStore.stationGames) ? contentInStore : await this.$store.dispatch('getContentStationGames', this.contentSelection);
      this.stationsIdsWithContentInstalled = contentWithStationGames.stationGames.reduce((installedStations, stationGame) => {
        if (stationGame.station) return [...installedStations, stationGame.station.id];
        return installedStations;
      }, []);
    } else {
      this.stationsIdsWithContentInstalled = null;
    }
  }

  updateStationIdSelectionBasedOnValidity () {
    let validStationIds = [...this.stationsIdsWithOngoingSessions];

    validStationIds = [...validStationIds, ...this.stationsWithTimerDisabled.map(station => station.id)];

    if (this.stationsIdsWithContentInstalled) {
      validStationIds = validStationIds.filter(stationId => this.stationsIdsWithContentInstalled.includes(stationId));
    }

    this.stationIdSelection = this.stationIdSelection.filter(station => validStationIds.includes(station));
  }
}
</script>

<style lang="postcss" scoped>
@import 'core/shared/styles';

.remote-launch {
  padding-top: var(--spacingMd);

  & .selections {
    display: grid;
    grid-gap: var(--spacingMd);
  }

  @media (--tablet) {
    & .content-selection {
      margin-bottom: var(--spacingMd);
    }
  }

  & .bottombarinfo {
    display: grid;
    grid-template-columns: repeat(2, 1fr);

    & .info-block {
      & .label {
        color: var(--colorManatee);
      }
    }
  }
}

.remote-launch.dark {
  background-color: var(--darkModeColorDarkBackground) !important;
  color: var(--darkModeColorText) !important;
}

.content-block.dark /deep/ .content .panel .content div .selections .content-selection .content .selectionradio .selectedcontent  {
  background-color: var(--darkModeColorBackground);
  color: #F7F8F8 !important;
}

.content-block.dark /deep/ .content .panel .content div .selections .station-selection .bookings .booking .box-and-title .title {
  color: #F7F8F8 !important;
}

</style>
